import { IntegrationLogo, IntegrationProvider } from '@clickguard/design-system'
import { AdAccountDTO } from 'api-models'
import { FadingScrollArea } from 'components/fading-scroll/fading-scroll-area'
import { ownsProtection } from 'components/protection-settings/protection-adapters'
import { adAccountLabel } from 'pages/user/settings/connections/connection-helpers'
import { ChevronRight } from 'components/icons'
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAssetStore } from 'store'
import { useShallow } from 'zustand/react/shallow'

/**
 * "{n} with their own settings" (DEV-2422 §1.1) — every ad account and campaign under
 * this website that is NOT inheriting, and the way into each one's settings.
 */

const heading = (count: number) => {
	if (count === 0) return 'None with their own settings'
	if (count === 1) return '1 with its own settings'
	return `${count} with their own settings`
}

const providerOf = (platform: string): IntegrationProvider =>
	platform === 'GADS'
		? 'google-ads'
		: platform === 'BADS'
		? 'microsoft'
		: 'meta'

interface OwnSettingsEntry {
	id: string
	name: string
	platform: IntegrationProvider
	/** `Ad account`, or `Campaign in {ad account name}`. */
	sublabel: string
	href: string
}

export const OwnSettingsList = () => {
	const navigate = useNavigate()
	const { asset, adAccounts, campaigns, demoMode } = useAssetStore(
		useShallow((state) => ({
			asset: state.asset,
			adAccounts: state.adAccounts,
			campaigns: state.campaigns,
			demoMode: state.demoMode
		}))
	)

	const accountId = demoMode ? 'workspace-demo' : asset?.account_id ?? ''
	const assetId = demoMode ? 'website-demo' : asset?.id ?? ''

	const entries = useMemo<OwnSettingsEntry[]>(() => {
		if (!accountId || !assetId) return []
		const connections = `/settings/workspace/${accountId}/asset/${assetId}/connections?tab=ad-accounts`
		const accounts: AdAccountDTO[] = adAccounts ?? []
		const accountById = new Map(
			accounts.map((account) => [account.id, account])
		)

		// `ownsProtection`, never a local test: it is the one place that knows a stored
		// object with no `inherit` key is an entity the ENGINE treats as owning its
		// settings, which this panel exists to list.
		const ownAccounts = accounts.filter(ownsProtection).map((account) => ({
			id: account.id,
			name: adAccountLabel(account),
			platform: providerOf(account.platform),
			sublabel: 'Ad account',
			href: `${connections}&adAccount=${account.id}`
		}))

		const ownCampaigns = (campaigns ?? [])
			.filter(ownsProtection)
			.map((campaign) => {
				const parent = accountById.get(campaign.ad_account_id)
				return {
					id: campaign.id,
					name: campaign.name,
					platform: providerOf(parent?.platform ?? 'FADS'),
					sublabel: parent
						? `Campaign in ${adAccountLabel(parent)}`
						: 'Campaign',
					href: `${connections}&adAccount=${campaign.ad_account_id}&campaign=${campaign.id}`
				}
			})

		return [...ownAccounts, ...ownCampaigns]
	}, [accountId, assetId, adAccounts, campaigns])

	// A short fade over the last few pixels while there IS more below — a hard cut
	// through a line of text reads as broken rather than as "there is more".
	const scrollBox = useRef<HTMLDivElement>(null)
	const [hasMore, setHasMore] = useState(false)
	useEffect(() => {
		const box = scrollBox.current
		if (!box) return
		const sync = () =>
			setHasMore(box.scrollTop + box.clientHeight < box.scrollHeight - 1)
		sync()
		box.addEventListener('scroll', sync, { passive: true })
		// The height arrives from the column outside, so it can change without this
		// list re-rendering.
		const observer =
			typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(sync)
		observer?.observe(box)
		return () => {
			box.removeEventListener('scroll', sync)
			observer?.disconnect()
		}
	}, [entries.length])

	// A real anchor, so the row keeps link semantics — middle-click and cmd-click open
	// a tab. The plain click is intercepted for the SPA push.
	const open = (href: string) => (event: MouseEvent<HTMLAnchorElement>) => {
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)
			return
		event.preventDefault()
		navigate(href)
	}

	// Hidden entirely when everything inherits (§1.1).
	if (entries.length === 0) return null

	return (
		// The same `rounded-lg bg-canvas p-3` shell as the Protective Actions box beside
		// it, so the two read as one row of two blocks.
		<div className="relative flex h-full w-full flex-col rounded-lg bg-[rgb(var(--cg-canvas))] p-3">
			<h3 className="shrink-0 text-base font-medium text-[rgb(var(--cg-text-primary))]">
				{heading(entries.length)}
			</h3>
			{/* `min-h-0 flex-1`: a flex child will not shrink under its content unless it
			    is told it may, and without it this box grows to every row and pushes
			    through the bottom of the card. */}
			<FadingScrollArea
				ref={scrollBox}
				className="-mx-1 mt-2 flex min-h-0 flex-1 flex-col divide-y divide-[rgb(var(--cg-border))] overflow-y-auto"
			>
				{entries.map((entry) => (
					<a
						key={entry.id}
						href={entry.href}
						onClick={open(entry.href)}
						// Row padding always EQUALS the scroll box's negative margin, so the
						// text lines up with the heading while the hover fill runs wider.
						// No radius: `divide-y` puts the hairline on the row's own top border
						// and a border follows the corner radius, which bends the divider.
						className="group flex min-w-0 shrink-0 items-center gap-2 px-1 py-1 transition-colors duration-150 hover:bg-[rgb(var(--cg-hover-overlay)/var(--cg-hover-alpha))] motion-reduce:transition-none"
					>
						{/* 16 is on the IntegrationLogo ladder (16/20/24). An off-ladder size
						    is NO size class, and the image renders at its intrinsic 300px. */}
						<span className="flex shrink-0 items-center">
							<IntegrationLogo provider={entry.platform} size={16} decorative />
						</span>
						<span className="flex min-w-0 flex-1 flex-col">
							<span className="truncate text-[12px] font-medium leading-4 text-[rgb(var(--cg-text-primary))]">
								{entry.name}
							</span>
							<span className="truncate text-[10px] leading-[14px] text-[rgb(var(--cg-text-secondary))]">
								{entry.sublabel}
							</span>
						</span>
						<ChevronRight className="h-3 w-3 shrink-0 text-[rgb(var(--cg-text-tertiary))]" />
					</a>
				))}
			</FadingScrollArea>
			{/* A sibling and not a `mask-image` on the scroll box, so it can carry an
			    opacity transition — a mask cannot fade in and out. */}
			<div
				aria-hidden="true"
				className={`pointer-events-none absolute inset-x-2 bottom-3 h-4 bg-gradient-to-t from-[rgb(var(--cg-canvas))] to-[rgb(var(--cg-canvas)/0)] transition-opacity duration-150 motion-reduce:transition-none ${
					hasMore ? 'opacity-100' : 'opacity-0'
				}`}
			/>
		</div>
	)
}
